import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Icon } from "@iconify/react";
import { useState } from "react";
import CommonFreeTextInput from "../../common/inputs/CommonFreeTextInput";
import CommonTextAreaInput from "../../common/inputs/CommonTextAreaInput";
import CommonDropdownInput from "../../common/inputs/CommonDropdownInput";
import { pointsConfig } from "../../../pointsConfig";
import PointsCreateMissionBookingCount from "./PointsCreateMissionBookingCount";
import PointsCreateMissionPlatformActions from "./PointsCreateMissionPlatformActions";
import PointsCreateMissionSalesTarget from "./PointsCreateMissionSalesTarget";
import PointsCreateMissionKYCCompletion from "./PointsCreateMissionKYCCompletion";
import PointsCreateMissionReferralCount from "./PointsCreateMissionReferralCount";
import PointsCreateMissionSupplierLoyalty from "./PointsCreateMissionSupplierLoyalty";
import PointCreateBadgeImageSelection from "./PointCreateBadgeImageSelection";
import { useMilesPoints } from "../../../hooks/useMilesPoints";
import { enqueueSnackbar } from "notistack";
import { badgesPayloadContructAndValidate } from "../../../controllers/pointsController";

const style = {
  position: "absolute",
  top: "50%",
  right: "0%",
  transform: "translate(0%, -50%)",
  width: window.innerWidth > 600 ? "40vw" : "100vw",
  bgcolor: "background.paper",
  boxShadow: 24,
};

export default function PointsUpdateBadgeModal({
  open,
  setOpen,
  callback,
  badge,
}) {
  const { updateBadge } = useMilesPoints();
  const [loading, setLoading] = useState(false);
  const badges = Object.values(pointsConfig.badges);
  const defaultData = {
    title: badge?.title || "",
    description: badge?.description || "",
    iconCode: badge?.iconCode || badges[0].code,
    type:
      badge?.type ||
      pointsConfig.missionCreation.missionTypes.bookingCount.code,
    supplierLoyalty: badge?.supplierLoyalty || {
      scope: "BOOKING_COUNT",
      supplier: "",
      amount: undefined,
    },
    salesTarget: badge?.salesTarget || {
      scopes: "ALL",
      targetAmount: undefined,
    },
    referralCount: badge?.referralCount || {
      referralCount: undefined,
      onlyAfterBooking: false,
    },
    bookingCount: badge?.bookingCount || [
      {
        label: "ALL",
        enabled: true,
        count: 0,
      },
      {
        label: "FLIGHT",
        enabled: false,
        count: 0,
      },
      {
        label: "HOTEL",
        enabled: false,
        count: 0,
      },
      {
        label: "TOUR",
        enabled: false,
        count: 0,
      },
    ],
    platformActions: badge?.platformActions || [
      {
        label: "ALL",
        enabled: true,
        count: 0,
      },
      {
        label: "LOGIN",
        enabled: false,
        count: 0,
      },
      {
        label: "QUOTE",
        enabled: false,
        count: 0,
      },
      {
        label: "COMPARE",
        enabled: false,
        count: 0,
      },
    ],
    kycCompletion: badge?.kycCompletion || {
      scopes: ["DOCUMENT_UPLOAD"],
    },
    timeFrame: badge?.timeFrame || "30 days",
    pointsRewarded: badge?.pointsRewarded || "",
  };
  const [data, setData] = useState(defaultData);

  const missionTypeOptions = [
    pointsConfig.missionCreation.missionTypes.bookingCount,
    pointsConfig.missionCreation.missionTypes.platformActions,
    pointsConfig.missionCreation.missionTypes.salesTarget,
    pointsConfig.missionCreation.missionTypes.kycCompletion,
    pointsConfig.missionCreation.missionTypes.referralCount,
    pointsConfig.missionCreation.missionTypes.supplierLoyalty,
  ];

  const handleClose = () => {
    setOpen(false);
  };

  const handleError = (message) => {
    enqueueSnackbar(message, {
      variant: "warning",
    });
  };

  const handleConfirm = async () => {
    const { payload, valid, message } = badgesPayloadContructAndValidate(data);
    if (!valid) return handleError(message);
    await updateBadge({
      payload,
      setLoading,
      id: badge?._id,
      callback: (badge) => {
        console.log({ badge });
        callback(badge);
        handleClose();
      },
    });
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <div className="flex flex-col font-main h-screen max-w-[100vw]">
            <div className="w-full max-w-[100vw] bg-primary1 p-4 text-white font-semibold flex justify-between">
              <span>Edit Badge</span>
              <button onClick={() => handleClose()}>
                <Icon
                  icon="material-symbols:close"
                  className={`text-secondary text-3xl`}
                />
              </button>
            </div>
            <div className="flex flex-1 flex-col overflow-y-scroll scroll-hide relative">
              <div className="flex w-full p-4 flex-col">
                <CommonFreeTextInput
                  label="Name"
                  placeholder="e.g Starter"
                  value={data.title}
                  onChange={(val) => setData({ ...data, title: val })}
                />
                <CommonTextAreaInput
                  label="Description"
                  className="mt-4"
                  value={data.description}
                  onChange={(val) => setData({ ...data, description: val })}
                />
                <PointCreateBadgeImageSelection
                  selected={data.iconCode}
                  setSelected={(selected) =>
                    setData({ ...data, iconCode: selected })
                  }
                  badges={badges}
                />
                <hr className="w-full border border-gray-300 my-4" />
                <CommonDropdownInput
                  label="Type"
                  options={missionTypeOptions}
                  optionObject
                  value={data.type}
                  onChange={(val) => setData({ ...data, type: val })}
                />

                <hr className="w-full border border-gray-300 my-4" />

                <div className="w-full flex flex-col">
                  {(() => {
                    switch (data.type) {
                      case pointsConfig.missionCreation.missionTypes
                        .bookingCount.code:
                        return (
                          <PointsCreateMissionBookingCount
                            bookingCount={data.bookingCount}
                            onBookingCountChange={(item) =>
                              setData({ ...data, bookingCount: item })
                            }
                            data={data}
                            setData={(item) =>
                              setData({
                                ...data,
                                timeFrame: item?.timeFrame,
                                pointsRewarded: item?.pointsRewarded,
                              })
                            }
                          />
                        );
                      case pointsConfig.missionCreation.missionTypes
                        .platformActions.code:
                        return (
                          <PointsCreateMissionPlatformActions
                            platformActions={data.platformActions}
                            onPlatformActionsChange={(item) =>
                              setData({ ...data, platformActions: item })
                            }
                            data={data}
                            setData={(item) =>
                              setData({
                                ...data,
                                timeFrame: item?.timeFrame,
                                pointsRewarded: item?.pointsRewarded,
                              })
                            }
                          />
                        );
                      case pointsConfig.missionCreation.missionTypes.salesTarget
                        .code:
                        return (
                          <PointsCreateMissionSalesTarget
                            salesTarget={data.salesTarget}
                            onSalesTargetChange={(item) =>
                              setData({ ...data, salesTarget: item })
                            }
                            data={data}
                            setData={(item) =>
                              setData({
                                ...data,
                                timeFrame: item?.timeFrame,
                                pointsRewarded: item?.pointsRewarded,
                              })
                            }
                          />
                        );
                      case pointsConfig.missionCreation.missionTypes
                        .kycCompletion.code:
                        return (
                          <PointsCreateMissionKYCCompletion
                            kycCompletion={data.kycCompletion}
                            onKycCompletionChange={(item) =>
                              setData({ ...data, kycCompletion: item })
                            }
                            data={data}
                            setData={(item) =>
                              setData({
                                ...data,
                                timeFrame: item?.timeFrame,
                                pointsRewarded: item?.pointsRewarded,
                              })
                            }
                          />
                        );
                      case pointsConfig.missionCreation.missionTypes
                        .referralCount.code:
                        return (
                          <PointsCreateMissionReferralCount
                            referralCount={data.referralCount}
                            onReferralCountChange={(item) =>
                              setData({ ...data, referralCount: item })
                            }
                            data={data}
                            setData={(item) =>
                              setData({
                                ...data,
                                timeFrame: item?.timeFrame,
                                pointsRewarded: item?.pointsRewarded,
                              })
                            }
                          />
                        );
                      case pointsConfig.missionCreation.missionTypes
                        .supplierLoyalty.code:
                        return (
                          <PointsCreateMissionSupplierLoyalty
                            supplierLoyalty={data.supplierLoyalty}
                            onSupplierLoyaltyChange={(item) =>
                              setData({ ...data, supplierLoyalty: item })
                            }
                            data={data}
                            setData={(item) =>
                              setData({
                                ...data,
                                timeFrame: item?.timeFrame,
                                pointsRewarded: item?.pointsRewarded,
                              })
                            }
                          />
                        );
                      default:
                        return null;
                    }
                  })()}
                </div>
              </div>
              <div className="flex-1"></div>
              <div
                className="w-full flex p-2 lg:p-4 flex-shrink-0 sticky bottom-0 bg-white gap-3 items-center"
                style={{
                  boxShadow:
                    "0 -4px 6px -1px rgba(0,0,0,0.1), 0 -2px 4px -2px rgba(0,0,0,0.1)",
                }}
              >
                <button
                  className="h-12 flex items-center justify-center w-20"
                  onClick={() => handleClose()}
                >
                  Close
                </button>
                <button
                  className="flex flex-1 items-center justify-center h-12 rounded-md text-white text-sm font-bold bg-primary1"
                  onClick={() => handleConfirm()}
                >
                  {loading ? (
                    <div className="buttonLoader"></div>
                  ) : (
                    <>Confirm</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
}
